import { useState, useEffect, useRef } from "react";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const linkColor = scrolled ? "text-foreground-800 hover:text-foreground-950" : "text-white/90 hover:text-white";
  const buttonBorder = scrolled ? "border-foreground-300 hover:border-foreground-500" : "border-white/40 hover:border-white/70";
  const buttonText = scrolled ? "text-foreground-800 hover:text-foreground-950" : "text-white/90 hover:text-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur-sm border-b border-background-200/60" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-6 lg:px-10 h-16 md:h-20">
        {/* Logo */}
        <a href="/" className={`flex items-center gap-2 transition-opacity hover:opacity-80 ${scrolled ? "text-foreground-950" : "text-white"}`}>
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`ri-leaf-line text-xl ${scrolled ? "text-primary-500" : "text-white"}`}></i>
          </div>
          <span className="font-heading font-semibold text-lg tracking-tight whitespace-nowrap">Mulebuy Spreadsheet</span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-2">
          <a href="/" className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${linkColor}`}>
            Home
          </a>

          <a href="/blog/what-is-mulebuy-spreadsheet" className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${linkColor}`}>
            Shopping Guide
          </a>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-5 py-2.5 border rounded-full transition-colors text-sm font-medium whitespace-nowrap ${buttonBorder} ${buttonText}`}
            >
              <span>Categories</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </div>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200 rounded-2xl shadow-lg py-2 w-56 max-h-[60vh] overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-primary-50/60 transition-colors"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right side buttons */}
        <div className="flex items-center gap-2 md:gap-4">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden md:inline-block text-sm font-medium transition-colors whitespace-nowrap ${linkColor}`}
          >
            Mulebuy Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full transition-colors ${
              scrolled ? "bg-primary-500 text-white hover:bg-primary-600" : "bg-white/20 text-white hover:bg-white/30"
            }`}
          >
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
              scrolled ? "text-foreground-800 hover:bg-background-100" : "text-white hover:bg-white/20"
            }`}
          >
            <i className={`text-lg ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-4 max-h-[70vh] overflow-y-auto">
          <div className="space-y-1 mb-4">
            <a
              href="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
            >
              <div className="w-4 h-4 inline-flex items-center justify-center mr-2">
                <i className="ri-home-line text-primary-500"></i>
              </div>
              Home
            </a>
            <a
              href="/blog/what-is-mulebuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
            >
              <div className="w-4 h-4 inline-flex items-center justify-center mr-2">
                <i className="ri-book-open-line text-primary-500"></i>
              </div>
              Shopping Guide
            </a>
          </div>

          <div className="border-t border-background-200/50 pt-3">
            <p className="px-4 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wide">Product Categories</p>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:text-foreground-950 hover:bg-primary-50/60 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                </div>
                <span>{cat.name}</span>
                <div className="w-4 h-4 flex items-center justify-center ml-auto">
                  <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                </div>
              </a>
            ))}
          </div>

          <div className="border-t border-background-200/50 pt-3 mt-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-foreground-950 text-white text-sm font-medium hover:bg-foreground-800 transition-colors"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line"></i>
              </div>
              <span>Visit Main Website</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}